import React from 'react';
import { bool, func, string, number, arrayOf } from 'prop-types';
import { submit, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { AlertHandler } from '@widergy/energy-ui';

import { actionCreators as accountActions } from 'redux/accountHandlers';
import { currentUserPropTypes } from 'redux/authHandlers';
import {
  ADD_ACCOUNT,
  ACCOUNT_RELATIONSHIP_VALIDATION,
  RELATED_ACCOUNTS,
  SERVICE_ADHERENCE
} from 'constants/reduxForms';
import { DIGITAL_BILL_FORM } from 'constants/general';
import { appConfig } from 'config/customization';
import UTModal from 'app/components/UTModal';
import { message } from 'utils/stringUtils';
import { trackEvent } from 'services/AnalyticsService';
import { categories, actions, labels } from 'constants/analytics';

import AddAccount from './components/AddAccount';
import AccountRelationshipValidation from './components/AccountRelationshipValidation';
import RelatedAccounts, { RELATED_ACCOUNT_LIST } from './components/RelatedAccounts';
import ServiceAdherence from './components/ServiceAdherence';
import styles from './styles.module.scss';

const ACCOUNT_LIST = 'accountList';

const existsWrongAnswer = (questions, values) =>
  questions.questions.some(
    question => values[question.key] !== question.field_options.find(answer => answer.correct).name
  );

class CreateAccount extends React.Component {
  componentWillReceiveProps = nextProps => {
    if (
      (this.props.initialAccountAdded !== nextProps.initialAccountAdded && nextProps.initialAccountAdded) ||
      (this.props.relatedAccountsAdded !== nextProps.relatedAccountsAdded && nextProps.relatedAccountsAdded)
    ) {
      const nextStep = this.handleRetrieveNextStep();
      if (nextStep) {
        this.props.dispatch(accountActions.addAccountUpdateStep(nextStep));
      } else {
        this.handleFinishFlow();
      }
    }
  };

  onSubmitAddAccount = values => {
    this.props.dispatch(accountActions.addAccountSetInitialAccount(values.clientNumber));
    const nextStep = this.handleRetrieveNextStep();
    if (nextStep) {
      this.props.dispatch(accountActions.addAccountUpdateStep(nextStep));
    } else {
      this.handleAddInitialAccount();
      this.handleFinishFlow();
    }
  };

  onValidateQuestions = async (values, _, payload) => {
    if (existsWrongAnswer(payload, values)) {
      AlertHandler.error(message('Signup:wrongAnswers'));
      this.handleFinishFlow();
    } else {
      this.handleAddInitialAccount();
    }
  };

  onSendRelatedAccounts = values => {
    if (values[RELATED_ACCOUNT_LIST] && values[RELATED_ACCOUNT_LIST].length > 0) {
      this.props.dispatch(accountActions.addAccounts(values[RELATED_ACCOUNT_LIST], false));
    }
  };

  onAcceptServiceAgreement = values => {
    const firstAccount = this.props.currentStep === 2;
    if (values.accountList && values.accountList.length > 0) {
      this.props.dispatch(
        accountActions.acceptServiceAgreement(DIGITAL_BILL_FORM, values.accountList, firstAccount)
      );
    } else {
      trackEvent(
        categories.FACTURACION,
        actions.NO_ADHESION_FACTURA_DIGITAL,
        labels.NO_ADHESION_FACTURA_DIGITAL_OK
      );
    }
    const nextStep = this.handleRetrieveNextStep();
    if (nextStep && firstAccount) {
      this.props.dispatch(accountActions.addAccountUpdateStep(nextStep));
    } else {
      this.handleFinishFlow();
    }
  };

  handleAddInitialAccount = async () =>
    this.props.dispatch(accountActions.addAccount(this.props.initialAccount));

  handleRetrieveNextStep = () => {
    let nextStep;
    if (this.props.currentStep === 0) {
      nextStep = appConfig.signUpFlow.AccountRelationshipValidation
        ? 1
        : appConfig.signUpFlow.ServiceAdherence
        ? 2
        : appConfig.signUpFlow.RelatedAccounts && 3;
    } else if (this.props.currentStep === 1) {
      nextStep = appConfig.signUpFlow.ServiceAdherence ? 2 : appConfig.signUpFlow.RelatedAccounts && 3;
    } else if (this.props.currentStep === 2) {
      nextStep = appConfig.signUpFlow.RelatedAccounts && 3;
    } else if (this.props.currentStep === 3) {
      nextStep = appConfig.signUpFlow.ServiceAdherence && 4;
    }
    return nextStep;
  };

  handleSendRelatedAccounts = () => this.props.dispatch(submit(RELATED_ACCOUNTS));

  handleAddAccount = () => this.props.dispatch(submit(ADD_ACCOUNT));

  handleAcceptServiceAgreement = () => this.props.dispatch(submit(SERVICE_ADHERENCE));

  handleValidateQuestions = () => this.props.dispatch(submit(ACCOUNT_RELATIONSHIP_VALIDATION));

  handleFinishFlow = () => {
    this.props.dispatch(accountActions.addAccountFinish());
    this.props.closeModal();
  };

  render() {
    const loading =
      this.props.relatedAccountsLoading ||
      this.props.addAccountLoading ||
      this.props.questionsLoading ||
      this.props.termsOfServiceLoading ||
      this.props.accountsLoading;

    const steps = [
      {
        component: AddAccount,
        acceptButton: {
          text:
            appConfig.signUpFlow.AccountRelationshipValidation || appConfig.signUpFlow.RelatedAccounts
              ? message('Accounts:next')
              : message('Accounts:add'),
          onPress: this.handleAddAccount
        },
        cancelButton: {
          text: message('Commons:cancel'),
          onPress: this.handleFinishFlow
        },
        onSubmit: this.onSubmitAddAccount
      },
      {
        component: AccountRelationshipValidation,
        acceptButton: {
          text: appConfig.signUpFlow.RelatedAccounts ? message('Accounts:next') : message('Accounts:add'),
          onPress: this.handleValidateQuestions
        },
        cancelButton: {
          text: message('Commons:cancel'),
          onPress: this.handleFinishFlow
        },
        onSubmit: this.onValidateQuestions,
        componentProps: { onCloseModal: this.handleFinishFlow }
      },
      {
        component: ServiceAdherence,
        acceptButton: {
          text:
            this.props.accountList && this.props.accountList.length > 0
              ? message('Commons:accept')
              : message('Signup:next'),
          onPress: this.handleAcceptServiceAgreement,
          className: styles.termsAndConditionsButton
        },
        onSubmit: this.onAcceptServiceAgreement
      },
      {
        component: RelatedAccounts,
        acceptButton: {
          text: message('Accounts:add'),
          onPress: this.handleSendRelatedAccounts,
          disabled: !this.props.selectedRelatedAccounts || this.props.selectedRelatedAccounts.length === 0
        },
        cancelButton: {
          text: message('Commons:cancel'),
          onPress: this.handleFinishFlow
        },
        onSubmit: this.onSendRelatedAccounts,
        componentProps: { onCloseModal: this.handleFinishFlow }
      },
      {
        component: ServiceAdherence,
        acceptButton: {
          text:
            this.props.accountList && this.props.accountList.length > 0
              ? message('Commons:accept')
              : message('Signup:next'),
          onPress: this.handleAcceptServiceAgreement,
          className: styles.termsAndConditionsButton
        },
        onSubmit: this.onAcceptServiceAgreement
      }
    ];

    const CurrentStep = steps[this.props.currentStep];
    const StepComponent = CurrentStep.component;
    return (
      <UTModal
        isOpen={this.props.modalIsOpen}
        onRequestClose={this.handleFinishFlow}
        contentLabel={message('Accounts:addAccount')}
        title={message('Accounts:addAccount')}
        acceptButton={!loading && CurrentStep.acceptButton}
        cancelButton={!loading && CurrentStep.cancelButton}
      >
        <StepComponent onSubmit={CurrentStep.onSubmit} {...CurrentStep.componentProps} />
      </UTModal>
    );
  }
}

CreateAccount.propTypes = {
  modalIsOpen: bool.isRequired,
  closeModal: func.isRequired,
  user: currentUserPropTypes,
  initialAccountAdded: bool.isRequired,
  relatedAccountsAdded: bool.isRequired,
  initialAccount: string,
  currentStep: number,
  accountList: arrayOf(string),
  selectedRelatedAccounts: arrayOf(string),
  relatedAccountsLoading: bool,
  addAccountLoading: bool,
  questionsLoading: bool,
  termsOfServiceLoading: bool,
  accountsLoading: bool
};

const formSelector = formValueSelector(SERVICE_ADHERENCE);
const relatedAccountsSelector = formValueSelector(RELATED_ACCOUNTS);

const mapStateToProps = store => ({
  user: store.auth.currentUser,
  currentStep: store.account.addAccountFlow.currentStep,
  initialAccount: store.account.addAccountFlow.initialAccount,
  initialAccountAdded: store.account.addAccountFlow.initialAccountAdded,
  relatedAccountsAdded: store.account.addAccountFlow.relatedAccountsAdded,
  accountsLoading: store.account.accountsLoading,
  accountList: formSelector(store, ACCOUNT_LIST),
  selectedRelatedAccounts: relatedAccountsSelector(store, RELATED_ACCOUNT_LIST),
  relatedAccountsLoading: store.account.relatedAccountLoading,
  addAccountLoading: store.account.addAccountLoading,
  questionsLoading: store.account.questionsLoading,
  termsOfServiceLoading: store.account.termsOfServiceLoading
});

export default connect(mapStateToProps)(CreateAccount);
